import { getStore } from '@/utils/dva'
import moment from 'moment'
import last from 'lodash/last'
import sortBy from 'lodash/sortBy'

export interface DefaultQueryConfig {
    name: string
    defaultShow?: boolean
    sort?: number
}
export interface FormatQueryConfig {
    name: string
    defaultShow?: boolean
    resetDefaultShow?: boolean
    sort?: number
    initSort: number
}

interface FieldDefaultShowConfigs extends DefaultQueryConfig {
    prevFieldName?: string
}

export enum Version {
    // 默认值 初始化值
    Init = '1970-01-02',
    V_2022_8_22 = '2022-08-25',
    V_2023_7_24 = '2023-08-21'
}

type TimeKey = 'userCreated' | 'checkInTime'

export interface DefaultQueryConfigVersion {
    version: Version
    timeKey: TimeKey
    list: DefaultQueryConfig[]
}

interface FormatSplit {
    split: any[]
    order: number
    name: string
    index: number
    prevFieldName?: string | undefined
    defaultShow?: boolean | undefined
    sort?: number | undefined
}

/**
 * 合并原始配置表以及初始化配置表（已过滤必须在原始配置表中）
 * 有个特点是，当用户在配置表中进行添加或者删除的时候，会自动影响到之前的老用户，比如增加一个字段时，
 * 老用户都会有这个字段显示，而删除同理
 * @param fields 原始配置
 * @param configArr 手动配置
 * @returns
 */
export const mergeFieldsAndConfigFields = (fields: any[], configArr: FieldDefaultShowConfigs[]) => {
    // 记住原始index
    const bIndexArr = configArr?.map(item => {
        const aNames = fields?.map(item => item?.name)
        const findIndex = aNames?.findIndex(name => name === item?.name)
        return { ...item, name: item.name, index: findIndex }
    }, [])

    // 对原始index进行记录并排序
    const formatOrder = bIndexArr?.map((item, index) => ({ ...item, order: index })).sort((a, b) => a?.index - b?.index)

    // 对于初始化配置表的第一项不是原始配置表第一项的特殊处理
    if (formatOrder[0]?.index > 0) {
        formatOrder?.unshift({ ...(fields[0] || {}), name: fields[0]?.name, index: 0, order: -1 })
    }

    // 对于原配置表进行处理，根据是否在原配置表里，对于defaultShow有不同的处理
    const mergeFields = fields?.map(item => {
        const findConfig = configArr?.find(({ name }) => name === item?.name)
        let defaultShow = true
        /**
         * 如果是defaultQueryConfig文件配置好的，则以配置的为主， 默认为false
         * 如果是没有在defaultQueryConfig文件配置过的，则以原始配置中的为主，默认为true
         */
        if (findConfig) {
            defaultShow = !!findConfig?.defaultShow
        } else {
            defaultShow = item?.initShow !== false
        }
        const newConfig = {
            ...(findConfig || {}),
            defaultShow
        }
        return {
            ...item,
            ...newConfig
        }
    })
    // 根据排好的序，进行数组的切割，确保可以进行配置表里新加跟随前一项
    const formatSplitArr = formatOrder
        .map((item, index) => {
            return {
                ...(item || {}),
                split: mergeFields?.slice(item?.index, formatOrder[index + 1]?.index)
            }
        })
        ?.sort((a, b) => a?.order - b?.order)

    // 汇总
    const finalDetailsArr = formatSplitArr?.reduce((prev: FormatSplit[], cur: FormatSplit) => {
        return [...prev, ...(cur?.split || [])]
    }, [])

    const finalArr = finalDetailsArr?.map(item => ({
        name: item?.name,
        defaultShow: item?.defaultShow,
        sort: item?.sort
    }))
    return {
        finalArr,
        finalDetailsArr
    }
}

export const onMergeConfigAndDefault = (config: DefaultQueryConfig[], fields: DefaultQueryConfig[]) => {
    const filterFieldsExisted =
        config?.filter(({ name }) => {
            return fields?.some(item => item?.name === name)
        }) || []

    const { finalArr, finalDetailsArr } = mergeFieldsAndConfigFields(fields, filterFieldsExisted)

    const formatSortDefaultQueryConfigVersion = finalArr?.map((item, index) => ({
        ...item,
        sort: index
    }))
    const formatSortDefaultQueryConfigVersionDetail = finalDetailsArr?.map((item, index) => ({
        ...item,
        sort: index
    }))

    return {
        formatSortDefaultQueryConfigVersion,
        formatSortDefaultQueryConfigVersionDetail
    }
}

/**
 *
 * 对原始的表单配置和已经格式化好的配置进行组合，并且排序
 * @param fields 表单配置项
 * @param configArr 对应的表单默认显示项的配置文件， 为null
 * 时表示默认全显
 * @returns 生成按规则配置后的默认配置
 */
export const transFormBaseFields = (
    fields: any[],
    configArr: FieldDefaultShowConfigs[],
    resetConfigArr: FieldDefaultShowConfigs[],
    resetConfig: DefaultQueryConfig[]
) => {
    const newFields = fields?.map((item, index) => {
        const findConfig = configArr?.find(({ name }) => name === item?.name)
        const getDefaultShow = () => {
            if (findConfig) {
                return !!findConfig?.defaultShow
            }

            return item?.initShow !== false
        }
        const getResetDefaultConfig = () => {
            const findResetConfig = resetConfig?.find(({ name }) => name === item?.name)
            const curConfig = resetConfigArr?.find(item => item?.name === findResetConfig?.name)
            const curResetConfig = resetConfigArr?.find(resetConfig => item?.name === resetConfig?.name)

            return {
                resetDefaultShow: findResetConfig ? !!curConfig?.defaultShow : item?.initShow !== false,
                resetDefaultSort: curResetConfig?.sort
            }
        }

        const newConfig: Partial<FormatQueryConfig> = {
            ...(findConfig || {}),
            // 无配置时默认全部显示
            defaultShow: getDefaultShow(),
            // 重置时，按最新的配置显示
            ...getResetDefaultConfig(),
            initSort: index
        }
        return {
            ...item,
            ...newConfig
        }
    })
    const sortResult = sortBy(newFields, 'sort')
    return sortResult
}

export const transFormBaseFieldsByUser = (fields: any[], defaultQueryConfigVersion: DefaultQueryConfigVersion[]) => {
    const store = getStore()
    const state = store?.getState()
    const {
        user: { currentUser }
    } = state || {}

    const formatInitList = fields?.map(item => ({ name: item?.name, defaultShow: item?.initShow !== false }))
    const mergeDefaultQueryConfigVersion = [
        {
            version: Version.Init,
            timeKey: 'userCreated',
            list: formatInitList
        },
        ...defaultQueryConfigVersion
    ]

    const findDefaultConfig = () => {
        const configsWithFormatTime = mergeDefaultQueryConfigVersion?.map(item => ({
            ...item,
            // 现在定死
            time: currentUser?.userCreated
        }))

        const findConfig = configsWithFormatTime?.find((curConfig, index) => {
            if (index + 1 === configsWithFormatTime?.length) {
                return true
            }
            if (
                moment(curConfig?.time).isAfter(curConfig?.version) &&
                moment(configsWithFormatTime[index + 1]?.time).isBefore(configsWithFormatTime[index + 1]?.version)
            ) {
                return true
            }
            return false
        })

        return findConfig
    }

    const config = findDefaultConfig()?.list || []
    const lastConfig = last(mergeDefaultQueryConfigVersion) as DefaultQueryConfigVersion
    const resetConfig = lastConfig.list || []

    const { formatSortDefaultQueryConfigVersion: mergeConfigAndDefault } = onMergeConfigAndDefault(config, fields)
    const { formatSortDefaultQueryConfigVersion: mergeConfigAndDefaultReset } = onMergeConfigAndDefault(
        resetConfig,
        fields
    )

    const transFormFields = transFormBaseFields(fields, mergeConfigAndDefault, mergeConfigAndDefaultReset, resetConfig)

    return transFormFields
}
